import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, ArrowLeft, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    key: 'free',
    name: 'The Scan',
    price: 0,
    headline: 'See where AI stands on you.',
    pitch: 'A one-time AI visibility scan across 9 engines. See your score, your top gaps, and what AI is saying about you right now.',
    features: [
      'AI Recommendation Score',
      'Gap analysis',
      'AI engine breakdown',
      'What AI says about you',
    ],
    limitations: [
      'No ongoing monitoring',
      'No alerts or weekly reports',
      'No content generation',
      'A snapshot, not a subscription',
    ],
    cta: 'Scan now',
    ctaStyle: 'outline',
  },
  {
    key: 'starter',
    name: 'The Monitor',
    price: 39,
    headline: 'Your AI presence, strengthened.',
    pitch: 'Weekly monitoring across 9 AI engines. Alerts when your position changes. Competitor tracking. Content that keeps you visible.',
    features: [
      'Weekly re-scans across all 9 engines',
      'Score timeline showing progress',
      'Real-time alerts when position changes',
      'Competitor tracking (1 competitor)',
      'Weekly email digest',
      'Basic content generation (2 posts/month)',
      'Gap analysis with fix guidance',
    ],
    limitations: [],
    cta: 'Start monitoring',
    ctaStyle: 'primary',
  },
  {
    key: 'professional',
    name: 'The Command Center',
    price: 79,
    popular: true,
    headline: 'The full system.',
    pitch: 'We don\'t just monitor — we actively build, optimize, and strengthen your AI presence across every platform.',
    features: [
      'Everything in The Monitor, plus:',
      'Unlimited content generation',
      '3 competitor tracking slots',
      'Review coaching and SMS requests',
      'Directory submissions',
      'Referral network',
      'Hallucination detection',
      'Voice search readiness monitoring',
      'Neighborhood authority content',
      'Priority re-scanning',
    ],
    limitations: [],
    cta: 'Get the full system',
    ctaStyle: 'primary-brass',
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentTier, setCurrentTier] = useState('free');
  const [checkingOut, setCheckingOut] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSub = async () => {
      try {
        if (user) {
          const res = await axios.get(`${API}/billing/subscription`, { withCredentials: true }).catch(() => ({ data: { tier: 'free' } }));
          setCurrentTier(res.data.tier || 'free');
        }
      } catch {} finally { setLoading(false); }
    };
    fetchSub();
  }, [user]);

  const handleUpgrade = async (planKey) => {
    if (!user) { navigate('/login'); return; }
    setCheckingOut(planKey);
    try {
      const res = await axios.post(`${API}/billing/create-checkout`, {
        plan: planKey,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch { setCheckingOut(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#EDE8DF' }}>
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDE8DF' }} data-testid="pricing-page">
      {/* Header */}
      <header className="px-6 md:px-10 pt-8 pb-4 max-w-5xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm hover:opacity-70 transition-colors mb-6" style={{ color: '#5C5347' }} data-testid="pricing-back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-center max-w-xl mx-auto">
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, color: '#3C3226', fontSize: 'clamp(28px, 4vw, 40px)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
            Choose your level of commitment
          </h1>
          <p className="mt-4" style={{ color: '#5C5347', fontSize: '15px', lineHeight: 1.7 }}>
            The scan gets you in the door. The monitoring keeps you ahead.
          </p>
        </div>
      </header>

      {/* 3-Column Pricing Grid */}
      <main className="px-6 md:px-10 max-w-5xl mx-auto py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((tier, i) => {
            const isCurrent = tier.key === currentTier;
            const canUpgrade = !isCurrent && tier.key !== 'free' && (currentTier === 'free' || (currentTier === 'starter' && tier.key === 'professional'));

            return (
              <motion.div
                key={tier.key}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex flex-col"
                data-testid={`plan-card-${tier.key}`}
              >
                {/* Popular badge */}
                {tier.popular && (
                  <div
                    className="text-center py-2 text-[11px] font-semibold uppercase tracking-widest"
                    style={{ backgroundColor: '#B8985A', color: '#2C2520' }}
                    data-testid="most-popular-badge"
                  >
                    Most Popular
                  </div>
                )}

                <div
                  className="flex-1 p-7 flex flex-col"
                  style={{
                    backgroundColor: '#F7F4EF',
                    border: tier.popular ? '2px solid #B8985A' : '1px solid #D4C9B8',
                    borderTop: tier.popular ? 'none' : undefined,
                    borderRadius: '2px',
                  }}
                >
                  {/* Tier Name */}
                  <p className="text-[11px] font-medium uppercase tracking-widest mb-3" style={{ color: '#8C7E6E' }}>{tier.name}</p>

                  {/* Price */}
                  <div className="mb-4">
                    {tier.price === 0 ? (
                      <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '48px', fontWeight: 300, color: '#3C3226' }}>Free</span>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '48px', fontWeight: 300, color: '#3C3226' }}>${tier.price}</span>
                        <span className="text-[14px]" style={{ color: '#8C7E6E' }}>/month</span>
                      </div>
                    )}
                  </div>

                  {/* Headline */}
                  <p className="text-[14px] font-medium mb-2" style={{ color: '#3C3226' }}>{tier.headline}</p>
                  <p className="text-[13px] mb-6" style={{ color: '#5C5347', lineHeight: 1.6 }}>{tier.pitch}</p>

                  {/* Features */}
                  <div className="space-y-2.5 mb-6 flex-1">
                    {tier.features.map((f, j) => (
                      <div key={j} className="flex items-start gap-2.5">
                        <Check size={14} strokeWidth={2.5} className="flex-shrink-0 mt-0.5" style={{ color: '#1D6B4F' }} />
                        <span className="text-[13px]" style={{ color: '#5C5347' }}>{f}</span>
                      </div>
                    ))}
                    {tier.limitations.map((l, j) => (
                      <div key={`l-${j}`} className="flex items-start gap-2.5">
                        <X size={14} strokeWidth={2} className="flex-shrink-0 mt-0.5" style={{ color: '#8C7E6E' }} />
                        <span className="text-[13px]" style={{ color: '#8C7E6E' }}>{l}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  {isCurrent ? (
                    <div className="text-center py-3 text-[12px] font-medium uppercase tracking-wider" style={{ border: '1px solid #1D6B4F', color: '#1D6B4F' }} data-testid={`current-plan-${tier.key}`}>
                      Your current plan
                    </div>
                  ) : canUpgrade ? (
                    <button
                      onClick={() => handleUpgrade(tier.key)}
                      disabled={checkingOut === tier.key}
                      className="w-full py-3.5 text-[12px] font-medium uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: '#2C2520',
                        color: '#F0EBE3',
                        borderRadius: 0,
                        boxShadow: tier.ctaStyle === 'primary-brass' ? '0 0 0 1px #B8985A' : 'none',
                      }}
                      data-testid={`upgrade-${tier.key}-btn`}
                    >
                      {checkingOut === tier.key ? (
                        <><Loader2 size={14} className="animate-spin" /> Processing...</>
                      ) : (
                        <>{tier.cta} <ArrowRight size={14} /></>
                      )}
                    </button>
                  ) : tier.key === 'free' ? (
                    <button
                      onClick={() => navigate('/onboarding')}
                      className="w-full py-3.5 text-[12px] font-medium uppercase tracking-wider transition-all hover:opacity-90 flex items-center justify-center gap-2"
                      style={{ border: '1px solid #D4C9B8', color: '#3C3226', borderRadius: 0 }}
                      data-testid="scan-now-btn"
                    >
                      Scan now <ArrowRight size={14} />
                    </button>
                  ) : null}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom note */}
        <p className="text-center mt-10" style={{ color: '#8C7E6E', fontSize: '12px' }}>
          All plans include your AI-optimized profile page. Cancel anytime.
        </p>
      </main>
    </div>
  );
}
